import { createStore, del, get, keys, set } from 'idb-keyval';

/**
 * Stylus notes, kept out of the main store.
 *
 * The original app inlined these PNGs as data-URLs inside the observation
 * records, which all live in one `localStorage` key. A school year of
 * handwritten notes overruns the ~5 MB quota, at which point every save fails
 * silently and work is lost. IndexedDB has no such ceiling.
 */
const store = createStore('jdc-anso-handwriting', 'png');

/** Read a stylus note as a PNG data-URL, or null if there is none. */
export async function getHandwriting(observationId: string): Promise<string | null> {
  return (await get<string>(observationId, store)) ?? null;
}

/** Store a stylus note. `dataUrl` is a full `data:image/png;base64,...` string. */
export async function putHandwriting(observationId: string, dataUrl: string): Promise<void> {
  await set(observationId, dataUrl, store);
}

export async function deleteHandwriting(observationId: string): Promise<void> {
  await del(observationId, store);
}

/** Ids of every locally held stylus note, used when building a JSON backup. */
export async function handwritingIds(): Promise<string[]> {
  return (await keys(store)) as string[];
}

/** Read several notes at once, skipping any that are missing. */
export async function getHandwritingMap(
  observationIds: readonly string[],
): Promise<Record<string, string>> {
  const entries = await Promise.all(
    observationIds.map(async (id) => [id, await getHandwriting(id)] as const),
  );

  const map: Record<string, string> = {};
  for (const [id, dataUrl] of entries) {
    if (dataUrl) map[id] = dataUrl;
  }
  return map;
}
